import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Circle,
  Clock,
  Eye,
  GitMerge,
  Link2,
  ListChecks,
  PackageOpen,
  Play,
  Plus,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { TicketDetail } from '@aurora/contracts';
import { del, get, patch, post, type ApiError } from '../../lib/api';
import { duration, formatAmount, relativeTime, shortDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import { cn } from '../../lib/cn';
import { useConfirm } from '../../components/dialog';
import { describeError, useToast } from '../../components/toast';
import { Badge, Button, Card, Input, Select, Skeleton } from '../../components/ui';
import { refreshTicket } from './detail-data';

/**
 * The panels that hang off a ticket: its task checklist, the effort logged
 * against it, who is watching it, what it is linked to, what was ordered, and
 * merging.
 *
 * Each fetches independently and fails independently — and says so. A panel
 * whose query failed used to read "No tasks yet", which is a claim about the
 * ticket rather than about the network.
 */

// ---------------------------------------------------------------------------
// Shared shell
// ---------------------------------------------------------------------------

function Panel({
  title,
  icon,
  count,
  action,
  children,
}: {
  title: string;
  icon: ReactNode;
  count?: number | string | undefined;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-line bg-sunken/40 px-3 py-2">
        <span aria-hidden className="text-ink-subtle">
          {icon}
        </span>
        <h2 className="text-body font-medium text-ink">{title}</h2>
        {count !== undefined && (
          <span className="rounded-full bg-raised px-1.5 py-px text-mini tabular-nums text-ink-muted">{count}</span>
        )}
        <div className="ms-auto">{action}</div>
      </div>
      {children}
    </Card>
  );
}

function PanelMessage({ children }: { children: ReactNode }) {
  return <p className="px-3 py-4 text-center text-body text-ink-muted">{children}</p>;
}

function PanelLoading() {
  return (
    <div className="space-y-2 p-3" aria-busy>
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

function PanelError({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-3">
      <p role="alert" className="text-body text-critical-ink">
        {what} could not be loaded.
      </p>
      <Button size="xs" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function PanelAlert({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="border-t border-line bg-critical-soft px-3 py-2 text-body text-critical-ink">
      {children}
    </p>
  );
}

/** A remove control that is visible on touch screens, where nothing hovers. */
const REMOVE_BUTTON =
  'shrink-0 rounded p-0.5 text-ink-subtle transition-opacity hover:text-critical-ink focus-visible:opacity-100 ' +
  'opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 disabled:opacity-40';

/** The text to show for a failure: the server's own reason, never "invalid fields". */
const reason = (error: unknown) => describeError(error).title;

/** A list from a reply, or an empty one — never a throw inside render that takes the ticket screen down. */
function asList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

interface Task {
  id: number;
  title: string;
  status: string;
  position: number;
  is_sequential: boolean;
  due_at: string | null;
  assignee_name: string | null;
  completed_by_name: string | null;
  logged_seconds: number;
}

const TASK_DONE = new Set(['completed', 'cancelled']);

export function TasksPanel({ number }: { number: string }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const confirm = useConfirm();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);

  const canManage = can('ticket.task.manage');

  const tasks = useQuery<{ data: Task[]; meta: { open: number; total: number } }, ApiError>({
    queryKey: ['ticket-tasks', number],
    queryFn: () => get(`/tickets/${number}/tasks`),
    enabled: can('ticket.task.view'),
  });

  // The server puts task changes on the timeline, so the timeline refreshes too.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['ticket-tasks', number] });
    void queryClient.invalidateQueries({ queryKey: ['ticket-timeline', number] });
  };

  const closeForm = () => {
    setAdding(false);
    setTitle('');
    addButton.current?.focus();
  };

  const create = useMutation<unknown, ApiError, string>({
    mutationFn: (value) => post(`/tickets/${number}/tasks`, { title: value }),
    onSuccess: () => {
      setError(null);
      closeForm();
      invalidate();
    },
    onError: (e) => setError(reason(e)),
  });

  const toggle = useMutation<unknown, ApiError, { id: number; status: string }>({
    mutationFn: ({ id, status }) => patch(`/tickets/${number}/tasks/${id}`, { status }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    // A sequential task refused out of order is the server telling the user
    // something true: "'Order laptop' has to be finished first".
    onError: (e) => setError(reason(e)),
  });

  const remove = useMutation<unknown, ApiError, Task>({
    mutationFn: (task) => del(`/tickets/${number}/tasks/${task.id}`),
    onSuccess: (_result, task) => {
      toast.success(`Removed “${task.title}”`);
      invalidate();
    },
    onError: (e) => setError(reason(e)),
  });

  if (!can('ticket.task.view')) return null;

  const list = asList<Task>(tasks.data?.data);
  const done = list.filter((t) => TASK_DONE.has(t.status)).length;

  return (
    <Panel
      title="Tasks"
      icon={<ListChecks className="size-3.5" />}
      count={list.length > 0 ? `${done}/${list.length}` : undefined}
      action={
        canManage && (
          <Button ref={addButton} size="xs" variant="ghost" onClick={() => (adding ? closeForm() : setAdding(true))}>
            <Plus aria-hidden className="size-3" />
            Add
          </Button>
        )
      }
    >
      {list.length > 0 && (
        <div className="h-0.5 bg-sunken" role="presentation">
          <div
            className="h-full bg-positive transition-[width] duration-300"
            style={{ width: `${Math.round((done / list.length) * 100)}%` }}
          />
        </div>
      )}

      {tasks.isLoading ? (
        <PanelLoading />
      ) : tasks.isError ? (
        <PanelError what="The tasks" onRetry={() => void tasks.refetch()} />
      ) : list.length === 0 && !adding ? (
        <PanelMessage>No tasks yet.</PanelMessage>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((task) => {
            const complete = task.status === 'completed';
            const cancelled = task.status === 'cancelled';
            return (
              <li key={task.id} className="group flex items-start gap-2 px-3 py-2">
                <button
                  type="button"
                  disabled={!canManage || (toggle.isPending && toggle.variables?.id === task.id)}
                  onClick={() => toggle.mutate({ id: task.id, status: complete ? 'open' : 'completed' })}
                  className="mt-px shrink-0 text-ink-subtle transition-colors hover:text-positive disabled:cursor-not-allowed disabled:hover:text-ink-subtle"
                  aria-label={complete ? `Reopen ${task.title}` : `Complete ${task.title}`}
                >
                  {complete ? <CheckCircle2 className="size-4 text-positive" /> : <Circle className="size-4" />}
                </button>

                <div className="min-w-0 flex-1">
                  <p
                    className={cn('text-body break-words text-ink', (complete || cancelled) && 'text-ink-subtle line-through')}
                    dir="auto"
                  >
                    {task.title}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-mini text-ink-subtle">
                    {task.assignee_name && <bdi>{task.assignee_name}</bdi>}
                    {task.due_at && (
                      <span
                        className={cn(!complete && new Date(task.due_at) < new Date() && 'font-medium text-critical-ink')}
                      >
                        due {shortDate(task.due_at)}
                      </span>
                    )}
                    {task.is_sequential && <span>in order</span>}
                    {task.logged_seconds > 0 && <span>{duration(task.logged_seconds)} logged</span>}
                  </div>
                </div>

                {canManage && (
                  <button
                    type="button"
                    disabled={remove.isPending && remove.variables?.id === task.id}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Delete the task “${task.title}”?`,
                        description: 'A deleted task cannot be brought back. Time logged against it stays on the ticket.',
                        confirmLabel: 'Delete task',
                        tone: 'danger',
                      });
                      if (ok) remove.mutate(task);
                    }}
                    className={REMOVE_BUTTON}
                    aria-label={`Delete task ${task.title}`}
                  >
                    <Trash2 aria-hidden className="size-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {adding && (
        <form
          className="border-t border-line p-2"
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              closeForm();
            }
          }}
          onSubmit={(event) => {
            event.preventDefault();
            if (title.trim()) create.mutate(title.trim());
          }}
        >
          <Input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs doing?"
            aria-label="Task title"
            maxLength={500}
          />
          <div className="mt-2 flex justify-end gap-1.5">
            <Button size="xs" variant="ghost" onClick={closeForm}>
              Cancel
            </Button>
            <Button size="xs" type="submit" loading={create.isPending} disabled={!title.trim()}>
              Add task
            </Button>
          </div>
        </form>
      )}

      {error && <PanelAlert>{error}</PanelAlert>}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

interface TimeEntry {
  id: number;
  duration_seconds: number;
  is_billable: boolean;
  note: string | null;
  agent_name: string | null;
  timer_started_at: string | null;
  performed_at: string;
}

export function TimePanel({ number }: { number: string }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const confirm = useConfirm();
  const [manual, setManual] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canManage = can('ticket.time_entry.manage');

  const entries = useQuery<
    { data: TimeEntry[]; meta: { total_seconds: number; scoped_to_self: boolean } },
    ApiError
  >({
    queryKey: ['ticket-time', number],
    queryFn: () => get(`/tickets/${number}/time`),
    enabled: can('ticket.time_entry.view'),
    // A running timer has to tick without a reload, or the number on screen is
    // wrong the moment it is drawn.
    refetchInterval: (query) => (query.state.data?.data?.some((e) => e.timer_started_at) ? 30_000 : false),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['ticket-time', number] });
    void queryClient.invalidateQueries({ queryKey: ['ticket-tasks', number] });
  };

  const start = useMutation<unknown, ApiError, void>({
    mutationFn: () => post(`/tickets/${number}/time/start`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(reason(e)),
  });

  const stop = useMutation<unknown, ApiError, void>({
    mutationFn: () => post(`/tickets/${number}/time/stop`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(reason(e)),
  });

  const log = useMutation<unknown, ApiError, number>({
    mutationFn: (seconds) => post(`/tickets/${number}/time`, { duration_seconds: seconds }),
    onSuccess: () => {
      setManual('');
      setError(null);
      invalidate();
    },
    onError: (e) => setError(reason(e)),
  });

  const remove = useMutation<unknown, ApiError, number>({
    mutationFn: (id) => del(`/tickets/${number}/time/${id}`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(reason(e)),
  });

  if (!can('ticket.time_entry.view')) return null;

  const list = asList<TimeEntry>(entries.data?.data);
  const running = list.find((e) => e.timer_started_at);
  const logged = list.filter((entry) => !entry.timer_started_at);
  const total = entries.data?.meta?.total_seconds ?? 0;

  return (
    <Panel
      title="Time"
      icon={<Clock className="size-3.5" />}
      count={total > 0 ? duration(total) : undefined}
      action={
        canManage && (
          <Button
            size="xs"
            variant={running ? 'danger' : 'ghost'}
            loading={start.isPending || stop.isPending}
            onClick={() => (running ? stop.mutate() : start.mutate())}
          >
            {running ? <Square aria-hidden className="size-3" /> : <Play aria-hidden className="size-3" />}
            {running ? 'Stop timer' : 'Start timer'}
          </Button>
        )
      }
    >
      {running && (
        <div className="flex items-center gap-2 border-b border-line bg-accent-soft px-3 py-2">
          <span className="size-1.5 animate-pulse rounded-full bg-accent" aria-hidden />
          <span className="text-mini text-accent-ink">Running since {relativeTime(running.timer_started_at)}</span>
        </div>
      )}

      {entries.isLoading ? (
        <PanelLoading />
      ) : entries.isError ? (
        <PanelError what="The time entries" onRetry={() => void entries.refetch()} />
      ) : logged.length === 0 && !running ? (
        <PanelMessage>No time logged.</PanelMessage>
      ) : (
        <ul className="divide-y divide-line">
          {logged.map((entry) => (
            <li key={entry.id} className="group flex items-baseline gap-2 px-3 py-1.5">
              <span className="w-14 shrink-0 text-body tabular-nums text-ink">{duration(entry.duration_seconds)}</span>
              <span className="min-w-0 flex-1 truncate text-mini text-ink-muted">
                <bdi>{entry.agent_name}</bdi>
                {entry.note ? (
                  <>
                    {' '}
                    — <bdi>{entry.note}</bdi>
                  </>
                ) : null}
              </span>
              {!entry.is_billable && (
                <Badge tone="neutral" className="shrink-0">
                  Not billable
                </Badge>
              )}
              {canManage && (
                <button
                  type="button"
                  disabled={remove.isPending && remove.variables === entry.id}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete ${duration(entry.duration_seconds)} of logged time?`,
                      description: 'The entry is removed for good and the ticket’s total goes down.',
                      confirmLabel: 'Delete entry',
                      tone: 'danger',
                    });
                    if (ok) remove.mutate(entry.id);
                  }}
                  className={REMOVE_BUTTON}
                  aria-label={`Delete ${duration(entry.duration_seconds)} logged by ${entry.agent_name ?? 'an agent'}`}
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <form
          className="flex items-center gap-1.5 border-t border-line p-2"
          onSubmit={(event) => {
            event.preventDefault();
            const seconds = parseDuration(manual);
            if (seconds !== null) log.mutate(seconds);
            else setError('Enter a length of time like 45m, 1h30m or 2h.');
          }}
        >
          <Input
            value={manual}
            onChange={(event) => setManual(event.target.value)}
            placeholder="45m, 1h30m"
            aria-label="Time to log"
            className="h-7 text-mini"
          />
          <Button size="xs" type="submit" loading={log.isPending} disabled={!manual.trim()}>
            Log time
          </Button>
        </form>
      )}

      {entries.data?.meta?.scoped_to_self && list.length > 0 && (
        <p className="border-t border-line px-3 py-1.5 text-mini text-ink-subtle">Showing your own entries only.</p>
      )}

      {error && <PanelAlert>{error}</PanelAlert>}
    </Panel>
  );
}

/**
 * Parses the durations people actually type.
 *
 * `1h30m`, `90m`, `1.5h`, `45`. Anything else — including nothing at all, a
 * zero-length entry — returns null so the caller can say so, rather than
 * silently logging a number the user did not mean.
 */
export function parseDuration(input: string): number | null {
  const text = input.trim().toLowerCase();
  if (!text) return null;

  if (/^\d+(\.\d+)?$/.test(text)) {
    const seconds = Math.round(Number(text) * 60);
    return seconds > 0 ? seconds : null;
  }

  const match = text.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+(?:\.\d+)?)\s*m)?$/);
  if (!match || (!match[1] && !match[2])) return null;

  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2] ?? 0);
  const seconds = Math.round(hours * 3600 + minutes * 60);
  return seconds > 0 ? seconds : null;
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

interface Watcher {
  user_id: number;
  full_name: string;
  email: string;
  reason: string;
}

const WATCH_REASONS: Record<string, string> = {
  cc: 'copied in',
  requester: 'requester',
  assignee: 'assignee',
};

export function WatchersPanel({ number }: { number: string }) {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const toast = useToast();

  const watchers = useQuery<{ data: Watcher[]; meta: { watching: boolean } }, ApiError>({
    queryKey: ['ticket-watchers', number],
    queryFn: () => get(`/tickets/${number}/watchers`),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['ticket-watchers', number] });

  const watch = useMutation<unknown, ApiError, void>({
    mutationFn: () => post(`/tickets/${number}/watchers`, {}),
    onSuccess: () => {
      toast.success('You are watching this ticket', 'You will be told about replies and changes.');
      invalidate();
    },
    onError: (e) => toast.error(e),
  });

  const unwatch = useMutation<unknown, ApiError, number>({
    mutationFn: (userId) => del(`/tickets/${number}/watchers/${userId}`),
    onSuccess: () => {
      toast.success('You stopped watching this ticket');
      invalidate();
    },
    onError: (e) => toast.error(e),
  });

  const list = asList<Watcher>(watchers.data?.data);
  const watching = watchers.data?.meta?.watching ?? false;

  return (
    <Panel
      title="Watchers"
      icon={<Eye className="size-3.5" />}
      count={list.length || undefined}
      action={
        !watchers.isError && (
          <Button
            size="xs"
            variant={watching ? 'secondary' : 'ghost'}
            loading={watch.isPending || unwatch.isPending}
            aria-pressed={watching}
            onClick={() => (watching && session ? unwatch.mutate(session.user.id) : watch.mutate())}
          >
            {watching ? 'Stop watching' : 'Watch'}
          </Button>
        )
      }
    >
      {watchers.isLoading ? (
        <PanelLoading />
      ) : watchers.isError ? (
        <PanelError what="The watchers" onRetry={() => void watchers.refetch()} />
      ) : list.length === 0 ? (
        <PanelMessage>Nobody is watching this ticket.</PanelMessage>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((watcher) => (
            <li key={watcher.user_id} className="flex items-center gap-2 px-3 py-1.5">
              <bdi className="min-w-0 flex-1 truncate text-body text-ink">{watcher.full_name}</bdi>
              {watcher.reason !== 'manual' && (
                <Badge tone="neutral" className="shrink-0">
                  {WATCH_REASONS[watcher.reason] ?? watcher.reason}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

interface LinkedTicket {
  id: number;
  number: number;
  subject: string;
  status: string;
  status_category: string;
  link_type: string;
  direction: 'incoming' | 'outgoing';
}

interface LinkedRecord {
  id: number;
  entity_type: string;
  number: string | null;
  title: string | null;
  relation: string;
}

interface ChildTicket {
  number: number;
  subject: string;
  status: string;
  status_category: string;
  assignee_name: string | null;
}

/** The link types a person can choose. */
const LINK_LABELS: Record<string, string> = {
  related: 'Related to',
  duplicate_of: 'Duplicate of',
  blocks: 'Blocks',
  blocked_by: 'Blocked by',
  caused_by: 'Caused by',
  causes: 'Causes',
};

/**
 * How a link reads from the other end.
 *
 * `duplicate_of` is not symmetric: when #10 is a duplicate of #4, #4 is the
 * original and is "duplicated by" #10. It used to read "Duplicate of #10" —
 * backwards.
 */
const INVERSE_LABELS: Record<string, string> = {
  related: 'Related to',
  duplicate_of: 'Duplicated by',
  blocks: 'Blocked by',
  blocked_by: 'Blocks',
  caused_by: 'Causes',
  causes: 'Caused by',
};

/** Where each kind of linked record lives; its number is appended. */
const RECORD_PATHS: Record<string, string> = {
  problem: '/problems',
  change: '/changes',
  release: '/releases',
  asset: '/assets',
};

const RECORD_NAMES: Record<string, string> = {
  problem: 'Problem',
  change: 'Change',
  release: 'Release',
  asset: 'Asset',
};

export function LinksPanel({ number }: { number: string }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const confirm = useConfirm();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [target, setTarget] = useState('');
  const [linkType, setLinkType] = useState('related');
  const [error, setError] = useState<string | null>(null);
  const linkButton = useRef<HTMLButtonElement>(null);
  const typeId = useId();

  const canLink = can('ticket.link');

  const links = useQuery<
    {
      data: {
        tickets: LinkedTicket[];
        records: LinkedRecord[];
        children: ChildTicket[];
        parent: { number: number; subject: string } | null;
      };
    },
    ApiError
  >({
    queryKey: ['ticket-links', number],
    queryFn: () => get(`/tickets/${number}/links`),
  });

  const invalidate = (other?: number) => {
    void queryClient.invalidateQueries({ queryKey: ['ticket-links', number] });
    void queryClient.invalidateQueries({ queryKey: ['ticket-timeline', number] });
    // The other end has a new link, and a new line on its timeline, too.
    if (other) {
      void queryClient.invalidateQueries({ queryKey: ['ticket-links', String(other)] });
      void queryClient.invalidateQueries({ queryKey: ['ticket-timeline', String(other)] });
    }
  };

  const closeForm = () => {
    setAdding(false);
    setTarget('');
    linkButton.current?.focus();
  };

  const create = useMutation<unknown, ApiError, { target_number: number; link_type: string }>({
    mutationFn: (body) => post(`/tickets/${number}/links`, body),
    onSuccess: (_result, body) => {
      setError(null);
      toast.success(`Linked #${body.target_number}`);
      closeForm();
      invalidate(body.target_number);
    },
    onError: (e) => setError(reason(e)),
  });

  const remove = useMutation<unknown, ApiError, { id: number; kind: 'ticket' | 'record'; other?: number; label: string }>({
    mutationFn: ({ id, kind }) => del(`/tickets/${number}/links/${id}?kind=${kind}`),
    onSuccess: (_result, { other, label }) => {
      setError(null);
      toast.success(`Unlinked ${label}`);
      invalidate(other);
    },
    onError: (e) => setError(reason(e)),
  });

  const data = links.data?.data;
  /**
   * Guarded at each step, not just at the root: a reply missing one of the
   * four lists must not throw inside render and take the whole ticket screen
   * down with it.
   */
  const total =
    (data?.tickets?.length ?? 0) + (data?.records?.length ?? 0) + (data?.children?.length ?? 0) + (data?.parent ? 1 : 0);

  const unlink = async (entry: { id: number; kind: 'ticket' | 'record'; other?: number; label: string }) => {
    const ok = await confirm({
      title: `Unlink ${entry.label}?`,
      description: 'Only the link is removed. Both records stay as they are.',
      confirmLabel: 'Unlink',
      tone: 'danger',
    });
    if (ok) remove.mutate(entry);
  };

  return (
    <Panel
      title="Related"
      icon={<Link2 className="size-3.5" />}
      count={total || undefined}
      action={
        canLink && (
          <Button ref={linkButton} size="xs" variant="ghost" onClick={() => (adding ? closeForm() : setAdding(true))}>
            <Plus aria-hidden className="size-3" />
            Link
          </Button>
        )
      }
    >
      {links.isLoading ? (
        <PanelLoading />
      ) : links.isError ? (
        <PanelError what="The related records" onRetry={() => void links.refetch()} />
      ) : total === 0 && !adding ? (
        <PanelMessage>Nothing linked.</PanelMessage>
      ) : (
        <div className="divide-y divide-line">
          {data?.parent && (
            <LinkRow
              label="Parent"
              to={`/tickets/${data.parent.number}`}
              reference={`#${data.parent.number}`}
              title={data.parent.subject}
            />
          )}

          {data?.children?.map((child) => (
            <LinkRow
              key={`child-${child.number}`}
              label="Child"
              to={`/tickets/${child.number}`}
              reference={`#${child.number}`}
              title={child.subject}
              meta={child.status}
              muted={child.status_category === 'closed'}
            />
          ))}

          {data?.tickets?.map((link) => (
            <LinkRow
              key={`link-${link.id}`}
              label={
                (link.direction === 'outgoing' ? LINK_LABELS[link.link_type] : INVERSE_LABELS[link.link_type]) ??
                'Related to'
              }
              to={`/tickets/${link.number}`}
              reference={`#${link.number}`}
              title={link.subject}
              meta={link.status}
              muted={link.status_category === 'closed'}
              onRemove={
                canLink
                  ? () => void unlink({ id: link.id, kind: 'ticket', other: link.number, label: `#${link.number}` })
                  : undefined
              }
            />
          ))}

          {data?.records?.map((record) => {
            const base = RECORD_PATHS[record.entity_type];
            const name = RECORD_NAMES[record.entity_type] ?? record.entity_type;
            return (
              <LinkRow
                key={`record-${record.id}`}
                label={name}
                // The record itself, not the module's list: the number is in
                // hand, and a link that lands on the Problems list is not a link.
                to={base && record.number ? `${base}/${record.number}` : (base ?? '/')}
                reference={record.number ? `#${record.number}` : ''}
                title={record.title ?? 'Untitled'}
                onRemove={
                  canLink
                    ? () =>
                        void unlink({
                          id: record.id,
                          kind: 'record',
                          label: record.number ? `${name} #${record.number}` : name.toLowerCase(),
                        })
                    : undefined
                }
              />
            );
          })}
        </div>
      )}

      {adding && (
        <form
          className="space-y-2 border-t border-line p-2"
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              closeForm();
            }
          }}
          onSubmit={(event) => {
            event.preventDefault();
            const parsed = Number(target.trim().replace(/^#/, ''));
            if (Number.isInteger(parsed) && parsed > 0) {
              create.mutate({ target_number: parsed, link_type: linkType });
            } else {
              setError('Enter a ticket number, like 1042.');
            }
          }}
        >
          <label htmlFor={typeId} className="sr-only-focusable">
            Link type
          </label>
          <Select id={typeId} size="sm" value={linkType} onChange={(event) => setLinkType(event.target.value)}>
            {Object.entries(LINK_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <div className="flex items-center gap-1.5">
            <Input
              autoFocus
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              placeholder="Ticket number"
              aria-label="Ticket number to link"
              inputMode="numeric"
              className="h-7 text-mini"
            />
            <Button size="xs" type="submit" loading={create.isPending} disabled={!target.trim()}>
              Link
            </Button>
            <Button size="xs" variant="ghost" onClick={closeForm} aria-label="Cancel linking">
              <X aria-hidden className="size-3" />
            </Button>
          </div>
        </form>
      )}

      {error && <PanelAlert>{error}</PanelAlert>}
    </Panel>
  );
}

function LinkRow({
  label,
  to,
  reference,
  title,
  meta,
  muted,
  onRemove,
}: {
  label: string;
  to: string;
  reference: string;
  title: string;
  meta?: string;
  muted?: boolean;
  onRemove?: (() => void) | undefined;
}) {
  return (
    <div className="group flex items-start gap-2 px-3 py-1.5">
      <div className="min-w-0 flex-1">
        <p className="text-mini text-ink-subtle">{label}</p>
        <Link
          to={to}
          className={cn('mt-px block truncate text-body text-ink hover:text-accent-ink hover:underline', muted && 'text-ink-subtle')}
        >
          <span className="tabular-nums text-ink-subtle">{reference}</span>{' '}
          {/* The reference is machine text; the subject is whatever they typed. */}
          <bdi>{title}</bdi>
        </Link>
        {meta && <p className="text-mini text-ink-subtle">{meta}</p>}
      </div>
      {onRemove && (
        <button type="button" onClick={onRemove} className={REMOVE_BUTTON} aria-label={`Unlink ${reference || label}`}>
          <X aria-hidden className="size-3.5" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Requested items
// ---------------------------------------------------------------------------

interface RequestedItem {
  id: number;
  item_name: string;
  quantity: number;
  stage: string;
  cost_per_unit: string | number | null;
}

const STAGES: Array<[string, string]> = [
  ['requested', 'Requested'],
  ['approved', 'Approved'],
  ['ordered', 'Ordered'],
  ['partially_fulfilled', 'Partly fulfilled'],
  ['fulfilled', 'Fulfilled'],
  ['delivered', 'Delivered'],
  ['cancelled', 'Cancelled'],
  ['returned', 'Returned'],
];
const STAGE_LABELS = Object.fromEntries(STAGES);

/**
 * What was ordered from the catalogue, and how far along it is.
 *
 * A request for "2 × Monitor" arrived with the item, the quantity and the cost
 * recorded and shown on no screen — the agent could not see what to order, or
 * mark it ordered or delivered.
 */
export function RequestedItemsPanel({ number }: { number: string }) {
  const queryClient = useQueryClient();
  const { can, session } = useSession();
  const toast = useToast();
  const mayEdit = Boolean(session?.is_agent) && can('ticket.edit');

  const items = useQuery<{ data: RequestedItem[]; meta: { total_cost: number } }, ApiError>({
    queryKey: ['ticket-items', number],
    queryFn: () => get(`/tickets/${number}/items`),
  });

  const stage = useMutation<unknown, ApiError, { item: RequestedItem; stage: string }>({
    mutationFn: ({ item, stage: next }) => patch(`/tickets/${number}/items/${item.id}`, { stage: next }),
    onSuccess: (_result, { item, stage: next }) => {
      toast.success(`${item.item_name}: ${STAGE_LABELS[next] ?? next}`);
      void queryClient.invalidateQueries({ queryKey: ['ticket-items', number] });
    },
    onError: (e) => toast.error(e),
  });

  if (items.isLoading) return null;
  if (items.isError) {
    return (
      <Panel title="Requested items" icon={<PackageOpen className="size-3.5" />}>
        <PanelError what="The requested items" onRetry={() => void items.refetch()} />
      </Panel>
    );
  }
  const list = asList<RequestedItem>(items.data?.data);
  // Most tickets were not raised from the catalogue; for them there is nothing to say.
  if (list.length === 0) return null;
  const total = items.data?.meta?.total_cost ?? 0;

  return (
    <Panel title="Requested items" icon={<PackageOpen className="size-3.5" />} count={list.length}>
      <ul className="divide-y divide-line">
        {list.map((item) => {
          const unit = item.cost_per_unit === null ? null : Number(item.cost_per_unit);
          return (
            <li key={item.id} className="space-y-1 px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <p dir="auto" className="min-w-0 text-body font-medium break-words text-ink">
                  {item.quantity > 1 ? `${item.quantity} × ` : ''}
                  {item.item_name}
                </p>
                {unit !== null && unit > 0 && (
                  <span className="shrink-0 text-mini tabular-nums text-ink-muted">{formatAmount(unit * item.quantity)}</span>
                )}
              </div>
              {mayEdit ? (
                <Select
                  size="sm"
                  aria-label={`Stage of ${item.item_name}`}
                  value={item.stage}
                  disabled={stage.isPending && stage.variables?.item.id === item.id}
                  onChange={(event) => stage.mutate({ item, stage: event.target.value })}
                >
                  {STAGES.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              ) : (
                <Badge tone="neutral">{STAGE_LABELS[item.stage] ?? item.stage}</Badge>
              )}
            </li>
          );
        })}
      </ul>
      {total > 0 && (
        <p className="border-t border-line px-3 py-1.5 text-mini text-ink-muted">Total {formatAmount(total)}</p>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

/**
 * Folds duplicate tickets into this one.
 *
 * A typo used to be enough to merge the wrong ticket: Enter merged at once, the
 * panel promised "the merge can be undone" and offered no way to undo it, and
 * the merged ticket kept no visible pointer to where its conversation went.
 * Now each ticket is looked up and named in a confirmation that says what will
 * happen, and every merge done here can be undone from here — or from the
 * merged ticket, which says where it went.
 */
export function MergeControl({ number, onMerged }: { number: string; onMerged: () => void }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const confirm = useConfirm();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState('');
  const [merged, setMerged] = useState<number[]>([]);
  const [skipped, setSkipped] = useState<Array<{ number: number; reason: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const inputId = useId();

  const merge = useMutation<
    { data: { merged: number[]; skipped: Array<{ number: number; reason: string }> } },
    ApiError,
    number[]
  >({
    mutationFn: (numbers) => post(`/tickets/${number}/merge`, { source_numbers: numbers }),
    onSuccess: (response) => {
      const result = response.data;
      setMerged((current) => [...current, ...result.merged]);
      setSkipped(result.skipped);
      setSources('');
      setError(null);
      if (result.merged.length > 0) {
        toast.success(
          `Merged ${result.merged.map((n) => `#${n}`).join(', ')} into #${number}`,
          'You can undo it from the Merge panel.',
        );
      }
      for (const source of result.merged) void queryClient.invalidateQueries({ queryKey: ['ticket', String(source)] });
      refreshTicket(queryClient, number, ['watchers', 'links']);
      onMerged();
    },
    onError: (e) => setError(reason(e)),
  });

  const undo = useMutation<unknown, ApiError, number>({
    mutationFn: (source) => post(`/tickets/${source}/unmerge`),
    onSuccess: (_result, source) => {
      setMerged((current) => current.filter((n) => n !== source));
      toast.success(`Undid the merge of #${source}`, `Its messages are back on #${source}.`);
      void queryClient.invalidateQueries({ queryKey: ['ticket', String(source)] });
      refreshTicket(queryClient, number, ['watchers', 'links']);
      onMerged();
    },
    onError: (e) => setError(reason(e)),
  });

  if (!can('ticket.merge')) return null;

  const submit = async () => {
    const numbers = [
      ...new Set(
        sources
          .split(/[\s,]+/)
          .map((part) => Number(part.replace(/^#/, '')))
          .filter((n) => Number.isInteger(n) && n > 0),
      ),
    ];
    if (numbers.length === 0) {
      setError('Enter one or more ticket numbers, like 1042.');
      return;
    }
    if (numbers.includes(Number(number))) {
      setError(`#${number} is this ticket. Enter the duplicates to fold into it.`);
      return;
    }

    // Each one looked up and named, so a mistyped number shows up as the wrong
    // subject in the confirmation instead of as a merged ticket.
    setChecking(true);
    setError(null);
    const found: Array<Pick<TicketDetail, 'number' | 'subject' | 'requester'>> = [];
    const missing: number[] = [];
    for (const candidate of numbers) {
      try {
        found.push((await get<{ data: TicketDetail }>(`/tickets/${candidate}`)).data);
      } catch {
        missing.push(candidate);
      }
    }
    setChecking(false);
    if (missing.length > 0) {
      setError(`${missing.map((n) => `#${n}`).join(', ')} could not be found, or you cannot open ${missing.length === 1 ? 'it' : 'them'}.`);
      return;
    }

    const ok = await confirm({
      title: `Merge ${found.length === 1 ? '1 ticket' : `${found.length} tickets`} into #${number}?`,
      description: (
        <span className="block space-y-2">
          <span className="block">
            Their replies and notes move to #{number}, and each one is closed with a pointer here. You can undo this
            afterwards.
          </span>
          <span className="block space-y-1">
            {found.map((ticket) => (
              <span key={ticket.number} className="block">
                <b className="font-medium">#{ticket.number}</b> · <bdi>{ticket.subject}</bdi>
                {ticket.requester ? (
                  <>
                    {' '}
                    · <bdi>{ticket.requester.name}</bdi>
                  </>
                ) : null}
              </span>
            ))}
          </span>
        </span>
      ),
      confirmLabel: found.length === 1 ? 'Merge 1 ticket' : `Merge ${found.length} tickets`,
    });
    if (ok) merge.mutate(found.map((ticket) => ticket.number));
  };

  return (
    <Panel
      title="Merge"
      icon={<GitMerge className="size-3.5" />}
      action={
        <Button size="xs" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? 'Close' : 'Merge in…'}
        </Button>
      }
    >
      {open ? (
        <form
          className="space-y-2 p-2"
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setOpen(false);
            }
          }}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <label htmlFor={inputId} className="text-mini font-medium text-ink-muted">
            Duplicates to fold into #{number}
          </label>
          <Input
            id={inputId}
            value={sources}
            onChange={(event) => setSources(event.target.value)}
            placeholder="#1042, #1050"
            className="h-7 text-mini"
          />
          <p className="text-mini leading-relaxed text-ink-muted">
            Their replies and notes move here. Each one is closed and points to this ticket, so its requester can
            still find it. You will be asked to confirm, and can undo a merge afterwards.
          </p>
          <Button size="xs" type="submit" loading={checking || merge.isPending} disabled={!sources.trim()}>
            Review merge
          </Button>
        </form>
      ) : merged.length === 0 ? (
        <PanelMessage>Fold duplicate tickets into this one.</PanelMessage>
      ) : null}

      {merged.length > 0 && (
        <ul className="divide-y divide-line border-t border-line">
          {merged.map((source) => (
            <li key={source} className="flex items-center justify-between gap-2 px-3 py-1.5 text-body">
              <span>
                Merged <Link to={`/tickets/${source}`} className="font-medium text-accent-ink hover:underline">#{source}</Link>
              </span>
              <Button
                size="xs"
                variant="ghost"
                loading={undo.isPending && undo.variables === source}
                onClick={() => undo.mutate(source)}
              >
                Undo
              </Button>
            </li>
          ))}
        </ul>
      )}

      {skipped.length > 0 && (
        <ul className="border-t border-line bg-caution-soft px-3 py-2 text-mini text-caution-ink">
          {skipped.map((entry) => (
            <li key={entry.number}>
              #{entry.number} was not merged: {entry.reason}.
            </li>
          ))}
        </ul>
      )}
      {error && <PanelAlert>{error}</PanelAlert>}
    </Panel>
  );
}
